import { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

// store
import { dbDataStore, messageAndErrorStore, userStore } from "../../store/Store";

// assets
import Close from "../../assets/close.svg";
import AddCircle from "../../assets/add_circle.svg";
import Trash from "../../assets/delete.svg";
import InputField from "../../formComponents/InputField";
import { useEscape } from "../../hooks/useEscape";
import FormButton from "../../formComponents/FormButton";
import { useVerifyUnique } from "../../hooks/useVerifyUnique";
import UpdateRequired from "../../components/UpdateRequired";
import Loading from "../../components/Loading";

export default function AddCompanyList(props) {
  // global
  const JwtToken = userStore((data) => data.JwtToken);
  const currentUser = userStore((data) => data.currentUser);
  const currentCompany = userStore((data) => data.currentCompany);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const typesData = dbDataStore((data) => data.typesData);
  const setCompanyLists = dbDataStore((data) => data.setCompanyLists);
  const companyLists = dbDataStore((data) => data.companyLists);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  // local
  let id = 0;
  if (props.id) {
    id = props.id;
  }
  const navigate = useNavigate();
  const [counter, setCounter] = useState(0);
  const [dataTypes, setDataTypes] = useState([]);
  const [listTypes, setListTypes] = useState([]);
  const [isPending, setIsPending] = useState(false);
  const [dataPending, setDataPending] = useState(false);
  const [oldName, setOldName] = useState("");
  const [listFields, setListFields] = useState([
    {
      id: 0,
      company_list_id: 0,
      field_name: "",
      data_type_id: 0,
      created_by_id: currentUser.id,
      updated_by_id: currentUser.id,
      field_key: "",
    },
  ]);
  const [list, setList] = useState({
    id,
    owner_company_id: currentCompany.id,
    list_name: "",
    list_type_id: 0,
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
  });

  // get list type and data type options
  useEffect(() => {
    let dataArr = [];
    let listArr = [];
    if (typesData) {
      typesData.forEach((t) => {
        if (t.name_of_tbl === "company_lists_tbl") {
          listArr.push({ value: t.id, label: t.type_name });
        } else if (t.name_of_tbl === "company_list_fields_tbl") {
          if (list.list_type_id === 35) {
            dataArr = [{ value: 38, label: "Decimal" }];
          } else {
            dataArr.push({ value: t.id, label: t.type_name });
          }
        }
        setDataTypes(dataArr);
        setListTypes(listArr);
      });
    }
  }, [typesData, list.list_type_id]);

  // Get list and fields if props.id
  useEffect(() => {
    if (id !== 0) {
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);
      const controller = new AbortController();
      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };
      setDataPending(true);
      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/company-list/${id}`,
        requestOptions
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              navigate("/login");
              return;
            } else {
              setDataPending(false);
              toggleNewMessage("Something went wrong");
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            setDataPending(false);
            return;
          } else {
            setDataPending(false);
            setOldName(data.company_list.list_name);
            setList(data.company_list);
            setCounter(data.company_list_fields.length - 1);
            setListFields(data.company_list_fields);
          }
        })
        .catch((err) => {
          console.log(err);
          toggleNewMessage("Something went wrong");
          setIsPending(false);
        })
        .finally(() => setDataPending(false));
      return () => controller.abort();
    }
  }, [id, setDataPending, JwtToken, setAuthStatus, navigate, toggleNewMessage]);

  const { exists } = useVerifyUnique(
    companyLists,
    "list_name",
    list.list_name,
    oldName
  );
  const handleClose = () => {
    props.setDocId("");
    setCounter(0);
    setListFields([]);
    setList({
      id,
      owner_company_id: currentCompany.id,
      list_name: "",
      list_type_id: 0,
      created_by_id: currentUser.id,
      updated_by_id: currentUser.id,
    });
    props.setTrigger(false);
  };

  useEscape(
    props.setTrigger,
    [setCounter, setListFields, setList, props.setDocId],
    [0, null, [], ""]
  );
  const handleChange = (e) => {
    setList({
      ...list,
      [e.target.name]: e.target.value,
    });
  };

  const handleField = (e, idx) => {
    if (e.target.name === `field_name${counter}`) {
      setListFields((prevData) => {
        const newData = [...prevData];
        newData[idx] = { ...newData[idx], field_name: e.target.value };
        return newData;
      });
    } else if (e.target.name === `field_key${counter}`) {
      setListFields((prevData) => {
        const newData = [...prevData];
        newData[idx] = { ...newData[idx], field_key: e.target.value };
        return newData;
      });
    }
  };

  const handleFieldType = (o, idx) => {
    setListFields((prevData) => {
      const newData = [...prevData];
      newData[idx] = { ...newData[idx], data_type_id: o.value };
      return newData;
    });
  };

  const handleDelete = () => {
    setCounter(counter - 1);
    setListFields((prevData) => {
      const newData = [...prevData];
      newData.pop();
      return newData;
    });
  };

  const handleAdd = () => {
    setCounter(counter + 1);
    setListFields((prevData) => {
      const newData = [...prevData];
      newData.push({
        id: 0,
        company_list_id: 0,
        field_name: "",
        data_type_id: 0,
        created_by_id: currentUser.id,
        updated_by_id: currentUser.id,
        field_key: "",
      });
      return newData;
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsPending(true);
    if (exists) {
      toggleNewMessage("List Name Already Exists");
      setIsPending(false);
      return;
    }
    // ensure field names are unique
    var fieldNames = [];
    var fieldKeys = [];

    var reqBody = {
      company_list: { ...list },
      company_list_fields: listFields.map((f) => ({ ...f })),
    };

    reqBody.company_list_fields.forEach((f, idx) => {
      if (fieldNames.includes(f.field_name)) {
        toggleNewMessage("Field Names Must be Unique");
        setIsPending(false);
        return;
      }
      if (fieldKeys.includes(f.field_key)) {
        toggleNewMessage("Field Keys Must be Unique");
        setIsPending(false);
        return;
      }
      fieldNames.push(f.field_name);
      if (f.field_key !== "") {
        fieldKeys.push(f.field_key);
      }
      if (list.list_type_id === 35) {
        reqBody.company_list_fields[idx].data_type_id = 38;
      }
    });

    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);

    const controller = new AbortController();
    var method = "POST";
    if (props.id) {
      method = "PATCH";
    }
    const requestOptions = {
      method,
      headers,
      signal: controller.signal,
      body: JSON.stringify(reqBody),
    };
    
    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/company-list/${id}`,
      requestOptions
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            navigate("/login");
            setIsPending(false);
            setAuthStatus(false);
          } else {
            toggleNewMessage("Something Went Wrong");
            setIsPending(false);
            return Promise.resolve();
          }
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (!data) {
          return;
        } else {
          setCompanyLists(data);
          toggleNewMessage(null, "List Added");
          setIsPending(false);
          props.setTrigger(false);
          return;
        }
      })
      .catch((err) => {
        toggleNewMessage("Something Went Wrong");
        console.log(err);
        setIsPending(false);
        return;
      })
      .finally(() => setIsPending(false));
    return () => controller.abort();
  };

  if (id !== 0 && dataPending) {
    return <Loading />;
  }
  return (
    <div className="popup">
      <div className="ratesheet-popup-inner">
        {!isPending && (
          <div>
            <img
              className="close"
              onClick={handleClose}
              src={Close}
              alt="close img"
            />
          </div>
        )}
        <div className="mb-3 info">
          <h4>Add List</h4>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-3 info">
            <InputField
              title="Name"
              className="info-label"
              type="text"
              name="list_name"
              value={list.list_name}
              onChange={handleChange}
              placeholder="Enter Name"
              required={true}
            />
            <label className="col-auto">
              <span>List Type</span>
              <Select
                id="list_type_id"
                options={listTypes}
                onChange={(option) =>
                  setList({
                    ...list,
                    list_type_id: option.value,
                  })
                }
                value={
                  listTypes.filter((x) => x.value === list.list_type_id)[0]
                }
                placeholder="List Type"
                className="dropdown"
              />
            </label>
          </div>
          {list.list_type_id !== 0 && (
            <div className="container text-center">
              <h5>Fields</h5>
              <div className="row">
                <div
                  className={`col-2 ${
                    list.list_type_id !== 35
                      ? "field-label"
                      : "field-label-single"
                  }`}
                >
                  Name
                </div>
                <div
                  className={`col-2 ${
                    list.list_type_id !== 35
                      ? "field-label"
                      : "field-label-single"
                  } d-flex justify-content-between`}
                >
                  Key
                  <UpdateRequired message={"Used for extra reference"} />
                </div>
                {list.list_type_id !== 35 && (
                  <div className="col-2 field-label">Type</div>
                )}
              </div>
              {Array.from(Array(counter + 1)).map((_, idx) => {
                return (
                  <div className="row" key={idx}>
                    <div className="col rate">
                      <input
                        name={`field_name${counter}`}
                        type="text"
                        value={listFields[idx].field_name || ""}
                        onChange={(e) => handleField(e, idx)}
                        placeholder="Field Name"
                        required
                      />
                    </div>
                    <div className="col rate">
                      <input
                        name={`field_key${counter}`}
                        type="text"
                        value={listFields[idx].field_key || ""}
                        placeholder="Field Key (optional)"
                        onChange={(e) => handleField(e, idx)}
                      />
                    </div>
                    {list.list_type_id !== 35 && (
                      <div className="col rate">
                        <Select
                          id="list_type_id"
                          options={dataTypes}
                          onChange={(o) => handleFieldType(o, idx)}
                          placeholder="Data Type"
                          value={dataTypes.filter(
                            (x) => x.value === listFields[idx].data_type_id
                          )}
                          required
                        />
                      </div>
                    )}
                    <div className="col-2 d-flex justify-content-between">
                      {idx !== 0 && idx === counter && (
                        <div className="mileage">
                          <img
                            id={idx}
                            className="delete-band"
                            alt="delete img"
                            src={Trash}
                            onClick={handleDelete}
                          />
                        </div>
                      )}
                      {idx === counter && (
                        <div className="mileage">
                          <img
                            id={idx}
                            className="delete-band"
                            alt="delete img"
                            src={AddCircle}
                            onClick={handleAdd}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <FormButton pending={isPending} name={"Submit"} />
        </form>
      </div>
    </div>
  );
}
